import { format } from 'date-fns';

import { getTeamOrPlayerImageUrl } from '~/domains/common/utils/getTeamOrPlayerImageUrl';

import type {
  APIPropChartData,
  PublicBaseballGame,
  PublicGameWithTeams,
} from '../../services/interfaces/propsService/api';
import { getLeagueFormat } from '../../services/interfaces/propsService/api/leagues';
import { getRoundFromMeta } from '../../services/interfaces/propsService/api/propMeta';
import type { TPropExpanded, TUserProp } from '../../services/interfaces/propsService/parsed/props';
import type { PropPickChartProps, PropPickProps } from '../../common/types/propPickProps.type';
import {
  QuickPicksApiPickSchema,
  normalizeMainPick,
} from '../services/interfaces/quickPicks.schemas';

import { getPlayerLineup } from './parseGroupSlip';
import {
  formatPlayerTeamFirstGameDescription,
  playerTeamIdForPick,
  teamIdForLeagueImage,
} from './playerTeamId.utils';

export { playerTeamIdForPick, teamIdForLeagueImage };

export function parseChartData(chartData: APIPropChartData, line?: number): PropPickChartProps {
  const data = chartData?.data ?? [];
  return {
    chartData: data.map((item) => ({
      label: new Date(item.date).toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
      }),
      secondaryLabel: item.label,
      value: item.score,
    })),
    lastXAvg: (data.reduce((acc, item) => acc + item.score, 0) / data.length).toFixed(1),
    ticks: Array.from(
      { length: chartData?.num_ticks ?? 0 },
      (_, i) => i * (chartData?.tick_step ?? 0)
    ),
    tickCount: chartData?.num_ticks ?? 0,
    ...(line ? { referenceLineValue: line } : {}),
  };
}

function parseBaseProps(prop: TPropExpanded | TUserProp) {
  const idForPlayerImage = prop.use_team_image
    ? teamIdForLeagueImage(prop) ?? prop.entity_id
    : prop.entity_id;
  const lineDisplay = prop.line_display?.trim();
  const firstName = prop.entity?.player_details.first_name || '';
  const lastName = prop.entity?.player_details.last_name || '';

  return {
    id: prop.id,
    playerId: prop.entity_id,
    league: prop.league,
    leagueImage: getTeamOrPlayerImageUrl(prop.league, teamIdForLeagueImage(prop)),
    playerImage: getTeamOrPlayerImageUrl(prop.league, idForPlayerImage),
    playerName: firstName && lastName ? `${firstName.slice(0, 1)}. ${lastName}` : lastName || firstName,
    startDate: format(prop.game_start, 'M/dd h:mm a'),
    startDateAsDate: new Date(prop.game_start),
    line: {
      value: prop.line,
      label: prop.type_display,
      type: prop.type,
      ...(lineDisplay ? { display: lineDisplay } : {}),
    },
    direction: (prop as TUserProp).direction,
    ...(prop.chart_data ? { chartProps: parseChartData(prop.chart_data, prop.line) } : {}),
    useTeamAlias: prop.use_team_image,
    teamAlias: prop.team?.alias,
    teamColor: prop.team?.primary_color,
    injury: prop.injury?.abbr_status,
    overProbability: prop.over_probability,
    underProbability: prop.under_probability,
  };
}

function parseTeamGameProps(prop: TPropExpanded | TUserProp, game: PublicGameWithTeams) {
  const playerTeamId = playerTeamIdForPick(prop, game);

  return {
    position: prop.entity?.player_details?.position,
    playerTeamId,
    awayTeam: {
      alias: game.away.alias ?? 'Away Team',
      id: game.away.id ?? 'team-away',
      points: game.away.points,
      record: game.away.record,
      lineup: game.away.lineup,
    },
    homeTeam: {
      alias: game.home.alias ?? 'Home Team',
      id: game.home.id ?? 'team-home',
      points: game.home.points,
      record: game.home.record,
      lineup: game.home.lineup,
    },
    gameDescription: formatPlayerTeamFirstGameDescription({
      homeTeam: game.home,
      awayTeam: game.away,
      playerTeamId,
      league: prop.league,
    }),
    ...(prop.game.league === 'mlb'
      ? (() => {
          const mlbGame = prop.game as PublicBaseballGame;
          return {
            weatherIcon: mlbGame.state?.weather_icon,
            playerLineup: getPlayerLineup(game.home.lineup, game.away.lineup, prop.entity_id),
          };
        })()
      : {}),
  };
}

function parseTournamentGameProps(prop: TPropExpanded | TUserProp) {
  const gameName = prop.game?.name ?? '';
  const round = getRoundFromMeta(prop.meta);

  return {
    playerTeamId: prop.entity_id,
    gameName,
    ...(round ? { round } : {}),
    gameDescription: [gameName, round != null ? `R${round}` : null].filter(Boolean).join(' • '),
  };
}

export function parsePropToComponentProp(prop: TPropExpanded | TUserProp): PropPickProps {
  const base = parseBaseProps(prop);
  const gameProps =
    getLeagueFormat(prop.league) === 'team'
      ? parseTeamGameProps(prop, prop.game as PublicGameWithTeams)
      : parseTournamentGameProps(prop);

  return normalizeMainPick(
    QuickPicksApiPickSchema.parse({ ...base, ...gameProps }) as PropPickProps
  );
}
